import { Layout, Menu, Dropdown, Space, type MenuProps } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { NAV_PROFILE, NAV_ITEMS } from "@/constants/navigation";
import { ROUTES } from "@/constants/routes";
import { ChevronDown, UserRound } from "lucide-react";
import logo from "@/assets/logo/logo.png";
import { useEffect, useState } from "react";
import { ChangePassword } from "@/features/admin/ChangePassword";
import { SignOut } from "@/features/admin/SignOut";
import { useAuthStore } from "@/store/auth.store";
import { useVillageList } from "@/features/admin/hook/useVillageList";

const { Header } = Layout;

const MENU_ROUTE_MAP: Record<string, string> = {
  "1": ROUTES.adminDashboard,
  "3": ROUTES.adminNotApplication,
  "4": ROUTES.adminAllApplication,
  "5": ROUTES.adminRejectApplication,
};

export const getAdminSelectedNavKey = (pathname: string) => {
  const villageRoutePrefix = ROUTES.adminVillageApplication.replace(
    "/:mdVillageId",
    "",
  );

  if (pathname.startsWith(villageRoutePrefix)) {
    return "2";
  }

  return (
    Object.entries(MENU_ROUTE_MAP).find(([, route]) => route === pathname)?.[0] ??
    "1"
  );
};

export default function AdminHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isSignOutOpen, setIsSignOutOpen] = useState(false);

  const { fetchVillageList, villageList } = useVillageList();

  useEffect(() => {
    fetchVillageList();
  }, []);
  const navItems = NAV_ITEMS(villageList ?? []);

  const selectedKey = getAdminSelectedNavKey(pathname);

  const handleProfileMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "6") return setIsChangePasswordOpen(true);

    if (key === "7") return setIsSignOutOpen(true);
  };

  return (
    <Header
      style={{ display: "flex", alignItems: "center", backgroundColor: "#FFF" }}
      className="px-10! shadow-lg"
    >
      <img src={logo} alt="Logo" className="h-6 w-auto" />

      <Menu
        mode="horizontal"
        style={{
          flex: 1,
          minWidth: 0,
          background: "transparent",
          borderBottom: 0,
        }}
        items={navItems}
        selectedKeys={[selectedKey]}
        onClick={({ key }) => {
          const keyString = String(key);

          // Handle village menu
          if (keyString.startsWith("village/")) {
            const mdVillageId = keyString.replace("village/", "");

            navigate(
              ROUTES.adminVillageApplication.replace(
                ":mdVillageId",
                mdVillageId,
              ),
            );

            return;
          }

          // Handle normal menu
          const route = MENU_ROUTE_MAP[keyString];

          if (route) {
            navigate(route);
          }
        }}
        className="pl-10! font-semibold hidden! lg:flex!"
      />

      <Dropdown
        menu={{ items: NAV_PROFILE, onClick: handleProfileMenuClick }}
        trigger={["click"]}
      >
        <a
          onClick={(e) => e.preventDefault()}
          style={{ color: "#272727" }}
          className="pl-4!"
        >
          <Space>
            <UserRound size={16} aria-hidden="true" />
            {useAuthStore.getState().user?.name}{" "}
            <ChevronDown size={16} aria-hidden="true" />
          </Space>
        </a>
      </Dropdown>

      <ChangePassword
        open={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />

      <SignOut open={isSignOutOpen} onClose={() => setIsSignOutOpen(false)} />
    </Header>
  );
}
